import { trackEvent } from "@hopper-b2b/api";
import { useExperiment, useTrackingProperties } from "@hopper-b2b/experiments";
import { ProtectedRoute } from "@hopper-b2b/hopper-auth";
import { darkModeIcons, lightModeIcons } from "@hopper-b2b/hopper-theming";
import {
  PATH_AGENT_IMPERSONATION,
  PATH_FLIGHTS,
  PATH_FLIGHTS_SEARCH,
  PATH_HOME,
  PATH_HOMES,
  PATH_HOTELS,
  PATH_HOTELS_ROOT,
  PATH_HOTELS_WEBAPP,
  PATH_PROFILE,
  PATH_STAYS,
  PATH_TRIPS,
  PATH_DISRUPTION_EXERCISE_PDA,
  apiConfig,
} from "@hopper-b2b/hopper-utils";
import {
  ClientName,
  FlightClientAssetProps,
  LaunchedApplicationProperties,
} from "@hopper-b2b/types";
import { LoadingScreenWithProgress } from "@hopper-b2b/ui";
import {
  FeatureFlagsContextProps,
  getDarkModePreferred,
  getLandingScreen,
  isHopperAppWebView,
  useAuthProvider,
  useChatButton,
  useDeviceTypes,
  useLaunchedApplicationEvent,
} from "@hopper-b2b/utilities";
import {
  PropsWithChildren,
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Redirect } from "react-router-dom";
import { Route, Routes } from "react-router-dom-v5-compat";

import clsx from "clsx";
import { FEATURE_FLAGS } from "../../Root";
import { colors } from "../../utils/colors";
import Maintenance from "../Maintenance/maintenance";
import styles from "./Body.module.scss";
import { useSupportChatbot } from "@hopper-b2b/chat";

const HomeRouteComponent = lazy(() => import("./HomeRouteComponent"));
const ImpersonationComponent = lazy(() => import("./ImpersonationComponent"));
const AirRouteComponent = lazy(() => import("./AirRouteComponent"));
const FlightSearchRoute = lazy(() => import("./SearchRoute/FlightSearchRoute"));
const HotelSearchRoute = lazy(() => import("./SearchRoute/HotelSearchRoute"));
const LodgingRouteComponent = lazy(() => import("./LodgingRouteComponent"));
const ProfileRouteComponent = lazy(() => import("./ProfileRouteComponent"));
const StaysHomesRouteComponent = lazy(
  () => import("./StaysHomesRouteComponent")
);

const TripsApp = lazy(() => import("@hopper-b2b/trips"));

export const Body = () => {
  const { matchesMobile } = useDeviceTypes();

  const { state } = useAuthProvider();
  const sessionInfo = state?.sessionInfo;

  const [launchEventSent, setLaunchEventSent] = useState(false);

  const isInMaintenance = useExperiment(FEATURE_FLAGS.MAINTENANCE);

  const enablePriceWatch = useExperiment(FEATURE_FLAGS.AIR_PRICE_WATCH);
  const enablePriceFreeze = useExperiment(FEATURE_FLAGS.AIR_PRICE_FREEZE);
  const enablePricePrediction = useExperiment(
    FEATURE_FLAGS.AIR_PRICE_PREDICTION
  );

  const enableScheduleChange = useExperiment(FEATURE_FLAGS.AIR_DISRUPTION);
  const enableMissedConnection = useExperiment(
    FEATURE_FLAGS.AIR_MISSED_CONNECTION
  );
  const enableAirTripInsurance = useExperiment(
    FEATURE_FLAGS.AIR_TRIP_INSURANCE
  );
  const enableLodgingTripInsurance = useExperiment(
    FEATURE_FLAGS.LODGING_TRIP_INSURANCE
  );

  const enableCfar = useExperiment(FEATURE_FLAGS.AIR_CFAR);
  const enableChfar = useExperiment(FEATURE_FLAGS.AIR_CHFAR);
  const enableVipSupport = useExperiment(FEATURE_FLAGS.VIP_SUPPORT);
  const enableDarkMode = useExperiment(FEATURE_FLAGS.APP_DARKMODE);
  const enableAirWallet = useExperiment(FEATURE_FLAGS.AIR_WALLET);
  const enableLodgingWallet = useExperiment(FEATURE_FLAGS.LODGING_WALLET);
  const enableLodgingPromotionsMerchandising = useExperiment(
    FEATURE_FLAGS.LODGING_PROMOTIONS_MERCHANDISING
  );
  const enableRatePlanTypeMerchandising = useExperiment(
    FEATURE_FLAGS.RATE_PLAN_TYPE_MERCHANDISING
  );

  const enableDisruptionProtection = useExperiment(
    FEATURE_FLAGS.AIR_DISRUPTION
  );
  const enableDisruptionProtectionUniversalExercise = useExperiment(
    FEATURE_FLAGS.Air_DISRUPTION_UNIVERSAL_EXERCISE_FLOW
  );
  const enableDisruptionProtectionUniversalRebook = useExperiment(
    FEATURE_FLAGS.Air_DISRUPTION_UNIVERSAL_REBOOK_FLOW
  );
  const showDisruptionDelayThresholdHighlightUI = useExperiment(
    FEATURE_FLAGS.DISRUPTION_DELAY_THRESHOLD_CHANGE_HIGHLIGHT
  );
  const enableAirShopV4 = true; //Default should use v4

  const enableAirExchange = useExperiment(FEATURE_FLAGS.AIR_EXCHANGE);
  const enableHFv2 = useExperiment(FEATURE_FLAGS.HFv2);

  const enableStays = useExperiment(FEATURE_FLAGS.STAYS);
  const enableHomesInStaysSearch = useExperiment(
    FEATURE_FLAGS.HOMES_IN_STAYS_SEARCH
  );
  const enableScrantonWebImprovedMerchandising = useExperiment(
    FEATURE_FLAGS.SCRANTON_WEB_IMPROVED_MERCHANDISING
  );
  const enableHotelCugDeals = useExperiment(FEATURE_FLAGS.HOTEL_CUG_DEALS);
  const enableProgressiveLodgingList = useExperiment(
    FEATURE_FLAGS.PROGRESSIVE_LODGING_LIST
  );
  const enableLodgingMetasearchOffers = useExperiment(
    FEATURE_FLAGS.LODGING_METASEARCH_OFFERS
  );

  const enableMealPlanSupport = useExperiment(FEATURE_FLAGS.MEAL_PLAN_SUPPORT);

  const enablePaymentsPayLaterForHotelsSmoketest = useExperiment(
    FEATURE_FLAGS.PAYMENTS_PAY_LATER_FOR_HOTELS_SMOKETEST
  );

  const trackingProperties: LaunchedApplicationProperties =
    useTrackingProperties({
      landing_screen: getLandingScreen(ClientName.HOPPER),
      url: document.location.pathname,
    });

  const launchSent = useLaunchedApplicationEvent(
    launchEventSent,
    trackingProperties,
    trackEvent
  );

  useEffect(() => {
    if (launchSent) {
      setLaunchEventSent(true);
    }
  }, [launchSent, setLaunchEventSent]);

  const supportChatbot = useSupportChatbot();
  const onSupportClick = useCallback(
    function () {
      // Only run in Protected paths
      supportChatbot?.openSupportChatbot();
    },
    [supportChatbot]
  );

  const featureFlags: FeatureFlagsContextProps = useMemo(
    () => ({
      enableAirExchange,
      enableCfar,
      enableChfar,
      enableDarkMode,
      enableHFv2,
      enableMissedConnection,
      enablePriceFreeze,
      enablePriceWatch,
      enablePricePrediction,
      enableScheduleChange,
      enableAirTripInsurance,
      enableLodgingTripInsurance,
      enableVipSupport,
      enableWallet: enableAirWallet,
      enableDisruptionProtection,
      enableDisruptionProtectionUniversalExercise,
      enableDisruptionProtectionUniversalRebook,
      enableAirShopV4,
      enableStays,
      enableHomesInStaysSearch,
      enableLodgingPromotionsMerchandising,
      enableRatePlanTypeMerchandising,
      showDisruptionDelayThresholdHighlightUI,
    }),
    [
      enableAirExchange,
      enableCfar,
      enableChfar,
      enableDarkMode,
      enableHFv2,
      enableMissedConnection,
      enablePriceFreeze,
      enablePriceWatch,
      enablePricePrediction,
      enableScheduleChange,
      enableAirTripInsurance,
      enableLodgingTripInsurance,
      enableVipSupport,
      enableAirWallet,
      enableDisruptionProtection,
      enableDisruptionProtectionUniversalExercise,
      enableDisruptionProtectionUniversalRebook,
      enableAirShopV4,
      enableStays,
      enableHomesInStaysSearch,
      enableLodgingPromotionsMerchandising,
      enableRatePlanTypeMerchandising,
      showDisruptionDelayThresholdHighlightUI,
    ]
  );

  const lodgingFeatureFlags: FeatureFlagsContextProps = useMemo(
    () => ({
      enableWallet: enableLodgingWallet,
      enableStays,
      enableLodgingPromotionsMerchandising,
      enableRatePlanTypeMerchandising,
      enableLodgingTripInsurance,
      enableScrantonWebImprovedMerchandising,
      enableHotelCugDeals,
      enableProgressiveLodgingList,
      enableLodgingMetasearchOffers,
      enableMealPlanSupport,
      enablePaymentsPayLaterForHotelsSmoketest,
    }),
    [
      enableLodgingTripInsurance,
      enableLodgingWallet,
      enableLodgingPromotionsMerchandising,
      enableRatePlanTypeMerchandising,
      enableStays,
      enableScrantonWebImprovedMerchandising,
      enableHotelCugDeals,
      enableProgressiveLodgingList,
      enableLodgingMetasearchOffers,
      enableMealPlanSupport,
      enablePaymentsPayLaterForHotelsSmoketest,
    ]
  );

  const flightAppClientAssets: FlightClientAssetProps = useMemo(
    () => ({
      sessionInfo: sessionInfo,
      assets:
        enableDarkMode && getDarkModePreferred()
          ? darkModeIcons
          : lightModeIcons,
    }),
    [sessionInfo, enableDarkMode]
  );

  const tripsAppClientAssets = useMemo(
    () => ({
      sessionInfo: sessionInfo,
      featureFlag: {},
      assets:
        enableDarkMode && getDarkModePreferred()
          ? darkModeIcons
          : lightModeIcons,
    }),
    [sessionInfo, enableDarkMode]
  );

  const isUserAuthenticated = useMemo(
    () => sessionInfo?.userScope?.isSignedIn,
    [sessionInfo]
  );

  return (
    <main
      className={clsx(styles.main, {
        [styles.withFooter]: !matchesMobile,
        [styles.mobile]: matchesMobile,
      })}
    >
      {isInMaintenance ? (
        <Maintenance />
      ) : (
        <Routes>
          <Route
            path={PATH_AGENT_IMPERSONATION + ":uid"}
            element={<ImpersonationComponent />}
          />

          <Route
            path={PATH_HOME}
            element={
              <ProtectedWrapper>
                <HomeRouteComponent />
              </ProtectedWrapper>
            }
          />
          <Route
            path={PATH_FLIGHTS}
            element={
              <ProtectedWrapper>
                <FlightSearchRoute />
              </ProtectedWrapper>
            }
          />
          <Route
            path={PATH_FLIGHTS_SEARCH}
            element={
              <ProtectedWrapper>
                <FlightSearchRoute />
              </ProtectedWrapper>
            }
          />
          <Route
            path={PATH_FLIGHTS + "*"}
            element={
              <ProtectedWrapper>
                <AirRouteComponent
                  clientAssets={flightAppClientAssets}
                  featureFlags={featureFlags}
                />
              </ProtectedWrapper>
            }
          />
          {
            // Webview /hotels redirect is handled in the /hotels/* route below
            !isHopperAppWebView() && (
              <Route
                path={PATH_HOTELS_ROOT}
                element={
                  <ProtectedWrapper>
                    <HotelSearchRoute />
                  </ProtectedWrapper>
                }
              />
            )
          }
          <Route
            path={PATH_HOTELS}
            element={
              <ProtectedWrapper>
                <LodgingRouteComponent
                  featureFlags={lodgingFeatureFlags}
                  //Do not use client assets, use TenantContext for icons and images
                />
              </ProtectedWrapper>
            }
          />
          <Route
            path={PATH_STAYS}
            element={
              <ProtectedWrapper>
                <LodgingRouteComponent featureFlags={lodgingFeatureFlags} />
              </ProtectedWrapper>
            }
          />
          <Route
            path={PATH_HOMES}
            element={
              <ProtectedWrapper>
                <StaysHomesRouteComponent />
              </ProtectedWrapper>
            }
          />
          <Route
            path={PATH_DISRUPTION_EXERCISE_PDA + "*"}
            element={
              <ProtectedWrapper>
                <TripsApp
                  apiConfig={apiConfig}
                  clientAssets={tripsAppClientAssets}
                  featureFlags={featureFlags}
                  colors={colors}
                  onSupportClick={onSupportClick}
                />
              </ProtectedWrapper>
            }
          />

          <Route
            path={PATH_TRIPS + "/*"}
            element={
              <ProtectedWrapper>
                <TripsApp
                  apiConfig={apiConfig}
                  clientAssets={tripsAppClientAssets}
                  featureFlags={featureFlags}
                  colors={colors}
                  onSupportClick={onSupportClick}
                />
              </ProtectedWrapper>
            }
          />

          <Route
            path={PATH_PROFILE}
            element={
              <ProtectedWrapper>
                {isUserAuthenticated ? (
                  <ProfileRouteComponent sessionInfo={sessionInfo} />
                ) : (
                  sessionInfo && <Redirect to={PATH_HOME} push={false} />
                )}
              </ProtectedWrapper>
            }
          />

          <Route
            path="*"
            element={
              <Redirect
                to={isHopperAppWebView() ? PATH_HOTELS_WEBAPP : PATH_HOME}
                push={false}
              />
            }
          />
        </Routes>
      )}
    </main>
  );
};

const ProtectedWrapper = ({ children }: PropsWithChildren) => {
  const { state } = useAuthProvider();

  return (
    <ProtectedRoute sessionInfo={state?.sessionInfo}>
      <Suspense fallback={<Loading />}>{children}</Suspense>
    </ProtectedRoute>
  );
};

const Loading = () => {
  return (
    <LoadingScreenWithProgress
      className="hopper"
      loadingSteps={[
        {
          title: "",
          description: "",
        },
      ]}
      loading
      showProgressBar={false}
    />
  );
};
